import { Capacitor } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'
import { formatMoney, formatPercent, formatShortDate } from './money'
import { tabCardFooterUrl } from './promo'
import type { RoastTone } from './roasts'
import type { Tab } from './types'
import type { WeeklyDamageReport } from './weeklySummary'

export type TabSharePayload = {
  venue: string
  date: string
  billCents: number
  tipCents: number
  tipPercent: number
  autoGratCents: number
  totalCents: number
  roastLine: string
  tone: RoastTone
}

export function tabSharePayloadFromSave(
  tab: Tab,
  roastLine: string,
  tone: RoastTone,
  totalCents: number,
): TabSharePayload {
  return {
    venue: tab.venue,
    date: tab.date,
    billCents: tab.billCents,
    tipCents: tab.tipCents,
    tipPercent: tab.tipPercent,
    autoGratCents: tab.autoGratCents ?? 0,
    totalCents,
    roastLine,
    tone,
  }
}

const W = 1080
const H = 1920

const COLORS = {
  bg: '#120e0c',
  panel: '#1c1612',
  foam: '#f4ead8',
  muted: '#b9a78a',
  faint: '#7a6b58',
  whiskey: '#e8a317',
  ember: '#ff5c38',
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/)
  const lines: string[] = []
  let line = ''
  for (const word of words) {
    const next = line ? `${line} ${word}` : word
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line)
      line = word
    } else {
      line = next
    }
  }
  if (line) lines.push(line)
  return lines
}

function drawLines(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  x: number,
  y: number,
  lineHeight: number,
): number {
  let cursor = y
  for (const line of lines) {
    ctx.fillText(line, x, cursor)
    cursor += lineHeight
  }
  return cursor
}

function headlineForTone(tone: RoastTone, tipPercent: number): string {
  if (tone === 'navigator') return 'Keys later.'
  if (tone === 'cheap') return `Tip shame · ${formatPercent(tipPercent)}`
  if (tone === 'generous' || tone === 'extra') return `Tip hero · ${formatPercent(tipPercent)}`
  return `Tab logged · ${formatPercent(tipPercent)} tip`
}

function drawBrandedFooter(ctx: CanvasRenderingContext2D, y: number): void {
  ctx.fillStyle = COLORS.faint
  ctx.font = '500 28px system-ui, sans-serif'
  ctx.fillText('Roasted on turns too → Navigator of Shame', 72, y)
  ctx.fillStyle = COLORS.muted
  ctx.font = '24px system-ui, sans-serif'
  const link = tabCardFooterUrl().replace(/^https:\/\//, '')
  ctx.fillText(link, 72, y + 40)
  ctx.fillStyle = COLORS.faint
  ctx.font = '22px system-ui, sans-serif'
  ctx.fillText('Tab of Shame · Shame Logic', 72, y + 88)
}

function drawCardFrame(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = COLORS.bg
  ctx.fillRect(0, 0, W, H)
  const gradient = ctx.createLinearGradient(0, 0, 0, H * 0.35)
  gradient.addColorStop(0, 'rgba(255, 92, 56, 0.22)')
  gradient.addColorStop(1, 'rgba(18, 14, 12, 0)')
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, W, H * 0.4)

  ctx.fillStyle = COLORS.panel
  ctx.strokeStyle = 'rgba(255, 92, 56, 0.35)'
  ctx.lineWidth = 3
  roundRect(ctx, 56, 120, W - 112, H - 280, 32)
  ctx.fill()
  ctx.stroke()
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

export function renderTabShareCard(payload: TabSharePayload): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas not supported')

  drawCardFrame(ctx)

  ctx.fillStyle = COLORS.whiskey
  ctx.font = '700 32px system-ui, sans-serif'
  ctx.fillText('TAB OF SHAME', 96, 200)
  ctx.fillStyle = COLORS.ember
  ctx.font = '600 26px system-ui, sans-serif'
  ctx.fillText('Not a real receipt · for laughs only', 96, 248)

  const venue = payload.venue.trim() || 'Unnamed watering hole'
  ctx.fillStyle = COLORS.foam
  ctx.font = '700 52px system-ui, sans-serif'
  const venueLines = wrapLines(ctx, venue, W - 192)
  let y = 320
  y = drawLines(ctx, venueLines.slice(0, 2), 96, y, 58)

  ctx.fillStyle = COLORS.muted
  ctx.font = '500 30px system-ui, sans-serif'
  ctx.fillText(formatShortDate(payload.date), 96, y + 16)

  ctx.fillStyle = COLORS.whiskey
  ctx.font = '700 44px system-ui, sans-serif'
  ctx.fillText(headlineForTone(payload.tone, payload.tipPercent), 96, y + 88)

  ctx.fillStyle = COLORS.foam
  ctx.font = '600 36px system-ui, sans-serif'
  ctx.fillText(`Bill ${formatMoney(payload.billCents)}`, 96, y + 160)
  if (payload.autoGratCents > 0) {
    ctx.fillText(`Auto-grat ${formatMoney(payload.autoGratCents)}`, 96, y + 210)
  }
  ctx.fillText(`Tip ${formatMoney(payload.tipCents)}`, 96, y + (payload.autoGratCents > 0 ? 260 : 210))
  ctx.font = '700 48px system-ui, sans-serif'
  ctx.fillStyle = COLORS.whiskey
  ctx.fillText(`You paid ${formatMoney(payload.totalCents)}`, 96, y + (payload.autoGratCents > 0 ? 340 : 290))

  ctx.fillStyle = COLORS.muted
  ctx.font = '500 32px system-ui, sans-serif'
  const roastLines = wrapLines(ctx, payload.roastLine, W - 192).slice(0, 5)
  drawLines(ctx, roastLines, 96, y + 420, 44)

  drawBrandedFooter(ctx, H - 160)
  return canvas
}

export function renderWeeklyShareCard(report: WeeklyDamageReport): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas not supported')

  drawCardFrame(ctx)

  ctx.fillStyle = COLORS.whiskey
  ctx.font = '700 32px system-ui, sans-serif'
  ctx.fillText('WEEKLY BAR TAX', 96, 200)
  ctx.fillStyle = COLORS.foam
  ctx.font = '700 56px system-ui, sans-serif'
  ctx.fillText(formatMoney(report.totalCents), 96, 300)
  ctx.font = '500 34px system-ui, sans-serif'
  ctx.fillStyle = COLORS.muted
  ctx.fillText('out the door (7 days)', 96, 360)

  ctx.fillStyle = COLORS.foam
  ctx.font = '600 36px system-ui, sans-serif'
  ctx.fillText(
    report.visitCount === 1 ? '1 visit' : `${report.visitCount} visits`,
    96,
    460,
  )
  ctx.fillText(`${formatMoney(report.tipCents)} tipped`, 96, 520)
  ctx.fillText(`Avg tip ${formatPercent(report.averageTipPercent)}`, 96, 580)

  ctx.fillStyle = COLORS.muted
  ctx.font = '500 30px system-ui, sans-serif'
  ctx.fillText('Top spot', 96, 680)
  ctx.fillStyle = COLORS.foam
  ctx.font = '700 42px system-ui, sans-serif'
  const topLines = wrapLines(ctx, report.topVenue, W - 192).slice(0, 2)
  drawLines(ctx, topLines, 96, 730, 50)

  ctx.fillStyle = COLORS.muted
  ctx.font = 'italic 32px system-ui, sans-serif'
  ctx.fillText('Your liver filed a complaint.', 96, 900)

  drawBrandedFooter(ctx, H - 160)
  return canvas
}

async function canvasToBase64(canvas: HTMLCanvasElement): Promise<string> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Could not create image'))
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        const dataUrl = reader.result as string
        resolve(dataUrl.split(',')[1] ?? '')
      }
      reader.onerror = () => reject(reader.error ?? new Error('Read failed'))
      reader.readAsDataURL(blob)
    }, 'image/png')
  })
}

/** Share a PNG via the system sheet (on device) or Web Share / download in browser. */
export async function shareCanvasImage(canvas: HTMLCanvasElement, title: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const base64 = await canvasToBase64(canvas)
    const path = `tab-of-shame-share-${Date.now()}.png`
    const written = await Filesystem.writeFile({
      path,
      data: base64,
      directory: Directory.Cache,
    })
    await Share.share({
      title,
      dialogTitle: title,
      files: [written.uri],
    })
    return
  }

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
  if (!blob) throw new Error('Could not create image')

  const file = new File([blob], 'tab-of-shame.png', { type: 'image/png' })
  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    await navigator.share({ files: [file], title })
    return
  }

  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = 'tab-of-shame.png'
  anchor.click()
  URL.revokeObjectURL(url)
}

export async function shareTabCard(payload: TabSharePayload): Promise<void> {
  const canvas = renderTabShareCard(payload)
  await shareCanvasImage(canvas, 'Tab of Shame')
}

export async function shareWeeklyCard(report: WeeklyDamageReport): Promise<void> {
  const canvas = renderWeeklyShareCard(report)
  await shareCanvasImage(canvas, 'Weekly bar tax')
}
